import { useState } from 'react'
import { CheckCircle, Upload, Calendar, FileText, HelpCircle } from 'lucide-react'
import Hero from '../components/Hero'
import SEO from '../components/SEO'

export default function Admissions() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    program: '',
    startDate: '',
    education: '',
    message: ''
  })

  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://rudovia.ac.zw'
      const response = await fetch(`${apiUrl}/api/send-application-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        setSubmitted(true)
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          program: '',
          startDate: '',
          education: '',
          message: ''
        })
        setTimeout(() => setSubmitted(false), 6000)
      } else {
        const errorData = await response.json()
        setError(errorData.error || 'Failed to submit application. Please try again.')
      }
    } catch (error) {
      console.error('Error:', error)
      setError('Error submitting application. Please check your connection.')
    }
  }

  const steps = [
    {
      icon: FileText,
      title: 'Complete Application',
      description: 'Fill out the online application form with your personal and educational information.'
    },
    {
      icon: Upload,
      title: 'Submit Documents',
      description: 'Upload required documents including transcripts, certificates, and recommendation letters.'
    },
    {
      icon: Calendar,
      title: 'Interview',
      description: 'Attend an interview with our admissions committee (in-person or virtual).'
    },
    {
      icon: CheckCircle,
      title: 'Receive Decision',
      description: 'Get your admission decision within 2-3 weeks of completing your application.'
    }
  ]

  const faqs = [
    {
      question: 'What are the application deadlines?',
      answer: 'Applications are accepted on a rolling basis. However, we recommend applying at least 3 months before your intended start date to ensure processing time.'
    },
    {
      question: 'Is financial aid available?',
      answer: 'Yes, we offer scholarships and financial aid packages to qualified students. Information about available aid is provided upon admission.'
    },
    {
      question: 'Can international students apply?',
      answer: 'Absolutely! We welcome international students and provide support with visa applications and accommodation arrangements.'
    },
    {
      question: 'What documents do I need to submit?',
      answer: 'Required documents include: high school/college transcripts, certificates, ID copy, passport photo, recommendation letters, and program-specific requirements.'
    },
    {
      question: 'Are online programs available?',
      answer: 'Yes, our Leadership program offers online learning options. Contact admissions for more details about online study.'
    }
  ]

  return (
    <>
      <SEO 
        title="Admissions - Apply to Rudovia Life College"
        description="Apply to Rudovia Life College. Learn about admission requirements, application process, and financial aid. Start your journey in Theology, Leadership, Nursing, Music Ministry, or Construction Management."
        keywords="apply Rudovia College, college admissions Zimbabwe, theology college application, nursing diploma application, music ministry application, construction management, online application"
      />
      <div className="pt-20">
        <Hero
          title="Admissions"
          subtitle="Begin your journey toward excellence and service"
          image="/assets/images/caleb-woods-SfN5KBvOpwY-unsplash.jpg"
          height="h-[400px]"
        />

        <section className="section-padding bg-white">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="heading-2 mb-4">Application Process</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Follow these simple steps to apply to Rudovia Life College
              </p>
            </div>
            <div className="grid md:grid-cols-4 gap-8">
              {steps.map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                    <step.icon className="w-10 h-10 text-white" />
                  </div>
                  <div className="text-3xl font-bold text-primary mb-2">{index + 1}</div>
                  <h3 className="text-xl font-bold text-primary mb-3">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section-padding bg-gray-50">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="heading-2 mb-4">Online Application Form</h2>
                <p className="text-xl text-gray-600">
                  Complete the form below to start your application
                </p>
              </div>
              <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
                {submitted && (
                  <div className="bg-green-50 border-2 border-green-500 rounded-lg p-8 flex flex-col items-center justify-center text-center min-h-96">
                    <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
                    <p className="text-green-900 font-bold text-2xl mb-3">
                      Application Submitted Successfully!
                    </p>
                    <p className="text-green-700 text-lg max-w-md">
                      Thank you for your application! We've received your submission and will contact you within 2-3 business days at the email address you provided. Check your inbox for a confirmation email.
                    </p>
                  </div>
                )}
                {error && (
                  <div className="bg-red-50 border-2 border-red-500 rounded-lg p-8 flex flex-col items-center justify-center text-center min-h-96">
                    <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mb-4">
                      <span className="text-white font-bold text-2xl">!</span>
                    </div>
                    <p className="text-red-900 font-bold text-2xl mb-3">
                      Submission Failed
                    </p>
                    <p className="text-red-700 text-lg max-w-md">
                      {error}
                    </p>
                  </div>
                )}
                {!submitted && !error && (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="firstName" className="block text-primary font-semibold mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-secondary focus:outline-none"
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-primary font-semibold mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-secondary focus:outline-none"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="email" className="block text-primary font-semibold mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-secondary focus:outline-none"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-primary font-semibold mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-secondary focus:outline-none"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="program" className="block text-primary font-semibold mb-2">
                        Program of Interest *
                      </label>
                      <select
                        id="program"
                        name="program"
                        value={formData.program}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-secondary focus:outline-none"
                      >
                        <option value="">Select a program</option>
                        <option value="certificate-ministry">Certificate in Ministry</option>
                        <option value="diploma-theology">Diploma in Theology</option>
                        <option value="degree-theology">Degree in Theology</option>
                        <option value="leadership">Leadership Programme</option>
                        <option value="nursing-diploma">Nursing Diploma</option>
                        <option value="music-ministry">Music Ministry Training Programme</option>
                        <option value="construction-management">Construction Management</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="startDate" className="block text-primary font-semibold mb-2">
                        Intended Start Date *
                      </label>
                      <select
                        id="startDate"
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-secondary focus:outline-none"
                      >
                        <option value="">Select start date</option>
                        <option value="feb-2026">February Intake</option>
                        <option value="aug-2026">August Intake</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="education" className="block text-primary font-semibold mb-2">
                      Highest Level of Education *
                    </label>
                    <select
                      id="education"
                      name="education"
                      value={formData.education}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-secondary focus:outline-none"
                    >
                      <option value="">Select education level</option>
                      <option value="high-school">High School Diploma</option>
                      <option value="associate">Associate Degree</option>
                      <option value="bachelor">Bachelor's Degree</option>
                      <option value="master">Master's Degree</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-primary font-semibold mb-2">
                      Personal Statement
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="5"
                      placeholder="Tell us about yourself, your goals, and why you want to study at Rudovia Life College..."
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-secondary focus:outline-none"
                    ></textarea>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6">
                    <div className="flex items-start space-x-3">
                      <Upload className="w-6 h-6 text-secondary flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-bold text-primary mb-2">Document Upload</h4>
                        <p className="text-gray-600 text-sm mb-3">
                          After submitting this form, you'll receive an email with instructions to upload required documents including transcripts, certificates, and recommendation letters.
                        </p>
                      </div>
                    </div>
                  </div>

                  <button type="submit" className="btn-primary w-full text-center">
                    Submit Application
                  </button>
                </form>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="section-padding bg-white">
          <div className="container-custom">
            <div className="text-center mb-12">
              <HelpCircle className="w-16 h-16 mx-auto mb-6 text-secondary" />
              <h2 className="heading-2 mb-4">Frequently Asked Questions</h2>
            </div>
            <div className="max-w-4xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                  <h3 className="text-xl font-bold text-primary mb-3">{faq.question}</h3>
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section-padding bg-gradient-to-r from-primary to-blue-900 text-white">
          <div className="container-custom text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Need Help with Your Application?</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Our admissions team is here to assist you. Contact us with any questions about the application process.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="mailto:admin@rudovia.ac.zw" className="btn-secondary">
                Email Admissions
              </a>
              <a href="/contact" className="btn-outline border-white text-white hover:bg-white hover:text-primary">
                Contact Us
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}
